/**
 * Central API layer for BiteWise frontend ↔ backend sync
 * Connects to backend routes defined in bitewise/server/index.ts
 */
import { getAuth, onAuthStateChanged, type User } from "firebase/auth";
import { logError } from "./logger";
import { captureError } from "./sentry";
import { enqueue, dequeue, peekAll } from "./queue";
// ✅ FIXED: Correct base depending on dev/prod
import { resolveApiBase } from "./apiBase";

const BASE = resolveApiBase();

function isLocalGuestSafeDishesGet(path: string, method?: string): boolean {
  if ((method || "GET").toUpperCase() !== "GET") return false;
  if (!path.startsWith("/dishes")) return false;
  if (typeof window === "undefined") return false;
  const host = String(window.location?.hostname || "").toLowerCase();
  const localHost = host === "localhost" || host === "127.0.0.1";
  return import.meta.env.DEV && localHost;
}

// --- Wait for Firebase to restore the user (used by authHeader) ---
async function waitForFirebaseUser(maxMs = 8000): Promise<User | null> {
  const auth = getAuth();
  if (auth.currentUser) return auth.currentUser;

  return await new Promise<User | null>((resolve) => {
    let unsub = () => {};
    const timer = setTimeout(() => {
      try {
        unsub();
      } catch {}
      resolve(getAuth().currentUser ?? null);
    }, maxMs);

    unsub = onAuthStateChanged(auth, (u) => {
      clearTimeout(timer);
      try {
        unsub();
      } catch {}
      resolve(u ?? null);
    });
  });
}

// --- Helper to attach Firebase ID token (with auto refresh on 401) ---
async function getBearerToken(forceRefresh = false): Promise<string | null> {
  let user = getAuth().currentUser;
  if (!user) {
    user = await waitForFirebaseUser(8000);
  }
  if (!user) {
    console.warn("[api] getBearerToken: no Firebase user available");
    return null;
  }

  try {
    const token = await user.getIdToken(forceRefresh);
    return token;
  } catch (err) {
    console.error("[api] getBearerToken: error getting idToken", err);
    return null;
  }
}

// --- Helper for safe response handling ---
async function handleResponse(res: Response) {
  const data = await res.json().catch(() => ({} as any));

  const isError = !res.ok || (data && data.ok === false);
  if (isError) {
    const msg =
      (data && data.error) || res.statusText || "Request failed";

    const authLike =
      res.status === 401 ||
      res.status === 403 ||
      typeof msg === "string" && msg.toLowerCase().includes("auth");

    if (authLike) {
      console.error("[api] handleResponse: auth error", {
        status: res.status,
        statusText: res.statusText,
        url: res.url,
        body: msg,
      });
    } else {
      console.error("[api] handleResponse: error", {
        status: res.status,
        statusText: res.statusText,
        url: res.url,
        body: msg,
      });
    }

    try {
      captureError(new Error("api_error"), {
        status: res.status,
        statusText: res.statusText,
        url: res.url,
        body: msg,
      });
    } catch {}

    throw new Error(msg);
  }

  return data;
}

// --- Generic helpers ---

// 🔧 Narrow headers to a simple object so spreads are type-safe
type RequestInitLite = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>;
  body?: string;
};

async function requestWithAuth(
  path: string,
  init: RequestInitLite,
  opts: { retry?: boolean } = {}
) {
  const retry = opts.retry !== false;
  const url = `${BASE}${path}`;

  const run = async (forceRefresh: boolean) => {
    const token = isLocalGuestSafeDishesGet(path, init.method)
      ? null
      : await getBearerToken(forceRefresh);

    const headers: Record<string, string> = {
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    if (!token) {
      console.warn("[api] requestWithAuth: no token for request", {
        path,
        method: init.method || "GET",
      });
    }

    console.log("[api] requestWithAuth -> fetch", {
      path,
      method: init.method || "GET",
      hasToken: !!token,
      url,
    });

    return fetch(url, {
      ...init,
      headers,
      credentials: "omit",
    });
  };

  // Offline handling
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const isWrite = init.method && init.method.toUpperCase() !== "GET";
    if (isWrite) {
      enqueue(path, init.body ? JSON.parse(String(init.body)) : {});
    }
    const offlineErr = new Error(
      "You appear to be offline. Please reconnect and try again."
    );
    captureError(offlineErr, {
      path,
      method: init.method || "GET",
      queued: isWrite,
    });
    throw offlineErr;
  }

  try {
    let res = await run(false);

    if (res.status === 401 && retry) {
      console.warn(
        "[api] requestWithAuth: 401 from backend, retrying with forceRefresh token",
        { path }
      );
      res = await run(true);
    }

    if (import.meta.env.DEV && path.startsWith("/dishes/home")) {
      console.debug("[api] response", { path, status: res.status });
    }

    return handleResponse(res as unknown as Response);
  } catch (err: any) {
    captureError(err, { path, method: init.method || "GET" });
    throw err;
  }
}

// Retry queued writes when back online
if (typeof window !== "undefined") {
  window.addEventListener("online", async () => {
    const items = peekAll();
    for (const item of items) {
      try {
        await apiPost(item.path, item.body);
        dequeue(item.id);
      } catch (err) {
        captureError(err, { path: item.path, retry: true });
      }
    }
  });
}

export async function apiGet(path: string, init?: RequestInitLite) {
  return requestWithAuth(
    path,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      ...(init || {}),
    },
    { retry: true }
  );
}

export async function apiPost(path: string, body: unknown) {
  const payload = JSON.stringify(body);
  return requestWithAuth(
    path,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
    },
    { retry: true }
  );
}

export async function apiDelete(path: string, body?: unknown) {
  const headers: Record<string, string> = {};
  let payload: string | undefined = undefined;

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  return requestWithAuth(
    path,
    {
      method: "DELETE",
      headers,
      body: payload,
    },
    { retry: true }
  );
}

// ------------------------------------------------------------------
// 🧭 User Profile / Coins
// ------------------------------------------------------------------

export async function getUserProfile() {
  try {
    return await apiGet(`/user/profile`);
  } catch (err) {
    logError("getUserProfile failed", { err: String(err) });
    throw err;
  }
}

export async function upsertProfile(profile: { name?: string; phone?: string }) {
  try {
    return await apiPost(`/user/profile`, profile);
  } catch (err) {
    logError("upsertProfile failed", { err: String(err), profile });
    throw err;
  }
}

export async function addCoins(amount: number, reason: string) {
  try {
    return await apiPost(`/user/coins/add`, { amount, reason });
  } catch (err) {
    logError(
      "addCoins failed",
      { err: String(err), reason, amount },
      { toast: true }
    );
    throw err;
  }
}

export async function getCoinsSummary() {
  try {
    return await apiGet(`/user/coins/summary`);
  } catch (err) {
    logError("getCoinsSummary failed", { err: String(err) });
    throw err;
  }
}

// ------------------------------------------------------------------
// 🤝 Referrals
// ------------------------------------------------------------------

export function getReferralStatus() {
  return apiGet(`/referral/status`);
}

export function createReferralCode() {
  return apiPost(`/referral/create`, {});
}

export function redeemReferralCode(code: string) {
  return apiPost(`/referral/redeem`, { code });
}

// ------------------------------------------------------------------
// 🎨 Themes / promos
// ------------------------------------------------------------------

export async function fetchThemesPublic() {
  const res = await fetch(`${resolveApiBase()}/themes`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false)
    throw new Error(data?.error || "Failed to fetch themes");
  return data.themes || [];
}

export function trackThemeEvent(name: string, event: "impression" | "click") {
  return fetch(`${resolveApiBase()}/themes/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, event }),
  }).catch(() => {});
}

// ------------------------------------------------------------------
// 📊 Analytics
// ------------------------------------------------------------------

export function getAnalyticsSummary(days = 7) {
  return apiGet(`/analytics/summary?days=${days}`);
}

// ------------------------------------------------------------------
// 🥇 Leaderboard / Achievements / Tasks
// ------------------------------------------------------------------

export function getLeaderboard() {
  return apiGet(`/leaderboard`);
}

export function getAchievements() {
  return apiGet(`/achievements`);
}

export function getTasks() {
  return apiGet(`/tasks`);
}

export function getMissionState() {
  return apiGet(`/missions/state`);
}

export function saveMissionState(payload: {
  dayKey: string;
  totalCompleted: number;
  streak: { current: number; best: number; lastDay: string | null };
  tasks: Array<{
    id: string;
    kind: string;
    title: string;
    target: number;
    reward: number;
    day: number;
    progress: number;
    ready: boolean;
    done: boolean;
    dueTs?: number | null;
  }>;
}) {
  return apiPost(`/missions/state`, payload);
}

// ------------------------------------------------------------------
// 🏠 User Addresses
// ------------------------------------------------------------------

export async function getAddresses() {
  const res = await apiGet(`/user/addresses`);
  return (res as any).data || (res as any).list || [];
}

export function addAddress(address: {
  id?: string;
  label: string;
  addressLine: string;
  lat: number;
  lng: number;
  active?: boolean;
}) {
  return apiPost(`/user/addresses`, address);
}

export function getNearest(lat: number, lng: number) {
  return apiGet(`/user/nearest?lat=${lat}&lng=${lng}`);
}

// ------------------------------------------------------------------
// 🍔 Orders (Outbound + Completion + History)
// ------------------------------------------------------------------

export function markOutbound(payload: {
  platform?: string;
  partner?: string;
  other_platform?: string;
  restaurant?: string;
  restaurant_id?: string;
  restaurant_url?: string;
  dish_name?: string;
  compare_row_id?: string;
  compare_pair_id?: string;
  target_url?: string;
  savings_kind?: 'verified' | 'estimated' | 'unknown';
  total?: number;
  otherTotal?: number;
  delta?: number;
  items?: Array<{ name: string; qty: number; price?: number }>;
  breakdown?: {
    subtotal?: number;
    discount?: number;
    fees?: number;
    tax?: number;
    delivery?: number;
    total?: number;
  };
}) {
  return apiPost(`/orders/outbound`, payload);
}

export function markCompletion(id: string, saved_amount: number) {
  return apiPost(`/orders/complete`, { id, saved_amount });
}

export function getOrders() {
  return apiGet(`/orders`);
}

// ------------------------------------------------------------------
// 📣 Feedback
// ------------------------------------------------------------------

export function submitFeedback(payload: {
  message: string;
  category?: string;
  steps?: string;
  screen?: string;
  severity?: string;
  deviceInfo?: string;
  screenshot?: { dataUrl: string; name?: string; type?: string; size?: number };
}) {
  return apiPost(`/feedback`, payload);
}

// ------------------------------------------------------------------
// 🛒 Cart sync (for server-side reminders)
// ------------------------------------------------------------------

export function syncCart(payload: {
  items: Array<{ id: string; name: string; qty: number; updatedAt?: number }>;
  updatedAt?: number;
}) {
  return apiPost(`/cart/sync`, payload);
}

// ------------------------------------------------------------------
// 🩺 Health / Debug endpoints
// ------------------------------------------------------------------
export function getHealth() {
  return apiGet(`/health`);
}

export function getDebugInfo() {
  return apiGet(`/debug/ready`);
}
